/**
 * What Pro gets you: one description of each feature, used everywhere Pro is advertised.
 *
 * There were three lists before this. ProFeatureTourModal had descriptions and videos for eleven
 * features, ProUpgradeModal had a bullet list of nine written differently (including two that are
 * not Pro feature keys at all), and `PRO_FEATURE_LABELS` knew of fourteen. So Alimony Analysis,
 * Word downloads and Equalize with Property were sold by none of them, and a customer could read
 * two different accounts of the same feature depending on which button they pressed.
 *
 * Labels still come from `PRO_FEATURE_LABELS`, which mirrors `ProFeatureNames.cs` on the server
 * and is what usage reporting keys off. This file adds the prose.
 */

import { PRO_FEATURE_LABELS, PRO_FEATURE_FLAG_MAP } from '../utils/proFeatureNames';

export interface ProFeature {
    /** Matches a key in PRO_FEATURE_LABELS, and a feature_name on the server. */
    key: string;
    label: string;
    description: string;
    /** Only some features have a demo recorded. The trial view never shows these. */
    videoPath?: string;
}

const DESCRIPTIONS: Record<string, string> = {
    CalculonChatBot:
        'Ask Calculon, your AI assistant, questions about child support calculations, guidelines, and statutory factors — directly within any case.',
    CalculonAllocationAdvicePdf:
        'Generate an AI-powered PDF that analyzes the allocation of expenses across both parties with detailed reasoning and recommendations.',
    SendCaseFile:
        'Share a complete case file with colleagues or co-counsel. Recipients can import the case directly into their own Calculist account.',
    CopyReceivedCase:
        'When you receive a shared case, duplicate it into your own case list with one click so you can edit it independently.',
    SendExpenseLinkToClient:
        'Send clients a personalized link to enter their monthly expenses online — no Calculist account or login required.',
    TeamLogoAndAccent:
        'Apply your firm\'s logo and brand accent color to all exported PDFs and documents for a polished, professional presentation.',
    HighIncomeEstimator:
        'Estimate child support obligations for high-income earners whose combined income exceeds standard guideline caps.',
    HighIncomeEstimatePdf:
        'Generate a professionally formatted PDF report for cases involving high-income child support estimates.',
    SplitCustody:
        'Handle split custody arrangements where each parent holds primary physical custody of at least one child.',
    ExpenseTemplates:
        'Save common monthly expense configurations as reusable templates and apply them across multiple cases instantly.',
    DivisionExclusions:
        'Clearly designate which expenses are excluded from income division calculations, with documented reasoning in the output.',
    AlimonyAnalysis:
        'Compare potential alimony amounts side by side against each party\'s after-tax income and anticipated expenses, with child support recalculated for each scenario. A planning workspace, not an Iowa guideline.',
    WordDocumentDownload:
        'Download case documents as editable Word files alongside the PDF versions, so you can adapt them in your own templates.',
    EqualizeWithProperty:
        'Balance a property division automatically by equalizing the net awarded to each party, rather than working the offsetting amount out by hand.',
};

const VIDEOS: Record<string, string> = {
    CalculonChatBot: 'https://pub-da77b52b45744471886b1840e1974960.r2.dev/demos/calculon.mp4',
};

/** The order features are presented in. Most compelling first; every key must have a description. */
const PRESENTATION_ORDER: string[] = [
    'CalculonChatBot',
    'CalculonAllocationAdvicePdf',
    'AlimonyAnalysis',
    'SendCaseFile',
    'CopyReceivedCase',
    'SendExpenseLinkToClient',
    'TeamLogoAndAccent',
    'HighIncomeEstimator',
    'HighIncomeEstimatePdf',
    'SplitCustody',
    'ExpenseTemplates',
    'DivisionExclusions',
    'EqualizeWithProperty',
    'WordDocumentDownload',
];

export const PRO_FEATURES: ProFeature[] = PRESENTATION_ORDER.map((key) => ({
    key,
    label: PRO_FEATURE_LABELS[key] ?? key,
    description: DESCRIPTIONS[key],
    videoPath: VIDEOS[key],
}));

/**
 * The features this deployment actually has switched on. A feature behind a flag that is off is
 * not something to advertise -- least of all to someone deciding whether to start a trial, who
 * would go looking for it and not find it.
 */
export function enabledProFeatures(flags: Record<string, boolean>): ProFeature[] {
    return PRO_FEATURES.filter((feature) => {
        const flagName = PRO_FEATURE_FLAG_MAP[feature.key];
        return !flagName || flags[flagName] !== false;
    });
}
